import { useEffect } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

/**
 * The marks and nodes a scratch block can hold: paragraphs, headings, lists,
 * bold and italic, plus undo. Deliberately NOT the script's extension set —
 * a block must never contain a scriptRow, because a block is loose thinking
 * and the Audio / Visual grid is the thing you promote it into.
 */
const scratchExtensions = [
  StarterKit.configure({
    heading: { levels: [1, 2, 3] },
    blockquote: false,
    code: false,
    codeBlock: false,
    horizontalRule: false,
    strike: false,
    underline: false,
    link: false,
    trailingNode: false,
  }),
];

interface Props {
  /** ProseMirror doc JSON. Read once on mount — the editor owns it after that. */
  initialBody: unknown;
  editable: boolean;
  onChange: (body: unknown) => void;
}

/**
 * One block's body editor. Toolbar-free by design: marks are keyboard only
 * (⌘B / ⌘I / list shortcuts), because a toolbar on every card would swamp a
 * 360px pane.
 */
export default function ScratchBody({ initialBody, editable, onChange }: Props) {
  const editor = useEditor({
    extensions: scratchExtensions,
    content: (initialBody ?? { type: 'doc', content: [{ type: 'paragraph' }] }) as never,
    // Never steal focus: a pane with ten blocks in it would fight the script
    // editor for the cursor on every render.
    autofocus: false,
    editable,
    onUpdate: ({ editor }) => onChange(editor.getJSON()),
  });

  useEffect(() => {
    editor?.setEditable(editable);
  }, [editor, editable]);

  return <EditorContent editor={editor} className="hsp-scratch-body" />;
}
